import React, { useEffect, useState } from "react";
import { T } from "../../constants/theme";

/**
 * DualInlineBarChart — two series side by side, same animate-from-bottom approach.
 */
const DualInlineBarChart = ({
  data,
  labelKey,
  keyA,
  keyB,
  colorA,
  colorB,
  labelA,
  labelB,
  formatValue,
  loading,
}) => {
  const [animated, setAnimated] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!loading && data && data.length > 0) {
      const t = setTimeout(() => setAnimated(true), 60);
      return () => clearTimeout(t);
    }
  }, [loading, data]);

  if (!data || data.length === 0) return null;

  const CHART_H = 52;
  const max = Math.max(...data.map((d) => Math.max(d[keyA], d[keyB])));

  return (
    <div style={{ marginBottom: "8px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "4px",
          height: `${CHART_H}px`,
        }}
      >
        {data.map((d, i) => {
          const hA = max > 0 ? Math.max(3, (d[keyA] / max) * CHART_H) : 3;
          const hB = max > 0 ? Math.max(3, (d[keyB] / max) * CHART_H) : 3;
          const hovA = hovered && hovered.i === i && hovered.key === "a";
          const hovB = hovered && hovered.i === i && hovered.key === "b";
          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                gap: "1px",
                position: "relative",
              }}
            >
              <div
                title={`${labelA}: ${formatValue(d[keyA])}`}
                onMouseEnter={() => setHovered({ i, key: "a" })}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flex: 1,
                  height: animated ? `${hA}px` : "3px",
                  background: hovA ? colorA : colorA + "cc",
                  borderRadius: "3px 3px 0 0",
                  transition: `height 0.65s cubic-bezier(0.22,1,0.36,1) ${i * 0.04}s, background 0.15s`,
                  cursor: "default",
                  position: "relative",
                }}
              >
                {hovA && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "calc(100% + 5px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#1a1a2e",
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 600,
                      padding: "3px 7px",
                      borderRadius: "5px",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      zIndex: 10,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {labelA}: {formatValue(d[keyA])}
                  </div>
                )}
              </div>
              <div
                title={`${labelB}: ${formatValue(d[keyB])}`}
                onMouseEnter={() => setHovered({ i, key: "b" })}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flex: 1,
                  height: animated ? `${hB}px` : "3px",
                  background: hovB ? colorB : colorB + "cc",
                  borderRadius: "3px 3px 0 0",
                  transition: `height 0.65s cubic-bezier(0.22,1,0.36,1) ${i * 0.04}s, background 0.15s`,
                  cursor: "default",
                  position: "relative",
                }}
              >
                {hovB && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "calc(100% + 5px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#1a1a2e",
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 600,
                      padding: "3px 7px",
                      borderRadius: "5px",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      zIndex: 10,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {labelB}: {formatValue(d[keyB])}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
        {data.map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: "9px",
              color: T.muted,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {d[labelKey]}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
        {[
          { color: colorA, label: labelA },
          { color: colorB, label: labelB },
        ].map((l, i) => (
          <span
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "10px",
              color: T.muted,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "10px",
                height: "10px",
                background: l.color,
                borderRadius: "2px",
              }}
            />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default DualInlineBarChart;
